import {
  captureSentryException,
  closeSentry,
  initSentry,
  isSentryInitialized,
  normalizeSentryPublicConfig,
  parseSentryPublicConfig,
  setSentryShopTag,
  type SentryPublicConfig,
} from '~/utils/sentry'

export default defineNuxtPlugin(async (nuxtApp) => {
  const runtimeConfig = useRuntimeConfig()
  const shopSlug = useShopSlug()
  const { fetchSettings, settings, loaded } = useSiteSettings()
  const platformApi = String(runtimeConfig.public.platformApiUrl || '').replace(/\/$/, '')

  async function loadPlatformSentry(): Promise<SentryPublicConfig | null> {
    if (!platformApi) return null
    try {
      const res = await $fetch<{ success?: boolean, data?: Partial<SentryPublicConfig> }>(
        `${platformApi}/public/sentry`,
      )
      if (res?.data) return normalizeSentryPublicConfig(res.data)
    } catch {
      // Custom-domain CORS or API down — fall back to shop public settings.
    }
    return null
  }

  async function loadSentryConfig(): Promise<SentryPublicConfig> {
    const fromPlatform = await loadPlatformSentry()
    if (fromPlatform) return fromPlatform

    if (!loaded.value) {
      await fetchSettings()
    }
    return parseSentryPublicConfig(settings.value.sentry)
  }

  async function syncSentry() {
    const config = await loadSentryConfig()
    if (!config.enabled || !config.dsn) {
      await closeSentry()
      return
    }

    if (!isSentryInitialized()) {
      await initSentry(config, {
        app: nuxtApp.vueApp,
        router: useRouter(),
      })
    }
    await setSentryShopTag(shopSlug.value)
  }

  nuxtApp.vueApp.config.errorHandler = (error, _instance, info) => {
    void captureSentryException(error, {
      vueInfo: info,
      shopSlug: shopSlug.value,
    })
  }

  if (import.meta.client) {
    window.addEventListener('unhandledrejection', (event) => {
      void captureSentryException(event.reason, {
        source: 'unhandledrejection',
        shopSlug: shopSlug.value,
      })
    })
  }

  watch(shopSlug, (slug) => {
    void setSentryShopTag(slug)
  })

  await syncSentry()
})
